import { Box, CircularProgress } from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";

import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import { useGetMediaURLQuery } from "../../redux/reducers/alerts.reducer";
import { IEvidenceImageURLDetails } from "../../types";

interface IPlateImageProps {
	eventId: string;
	plateCrop?: number[];
	plateImageName?: string;
	sourceImages?: IEvidenceImageURLDetails[];
}

const PlateImage: FC<IPlateImageProps> = (props) => {
	const { eventId, plateCrop, plateImageName, sourceImages } = props;

	const [showError, setShowError] = useState<boolean>(false);
	const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
	const [isCropping, setIsCropping] = useState<boolean>(false);
	const [cropFailed, setCropFailed] = useState<boolean>(false);

	const canCrop = !!plateCrop && plateCrop.length === 4 && !!plateImageName && !!sourceImages?.length;

	const sourceImage = useMemo<IEvidenceImageURLDetails | undefined>(() => {
		if (!canCrop || !plateImageName || !sourceImages) return undefined;

		return sourceImages.find((item) => item.file_name === plateImageName);
	}, [canCrop, plateImageName, sourceImages]);

	const useCroppedImage = canCrop && !!sourceImage && !cropFailed;

	const {
		data: getImageURLResponse,
		isLoading: getImageURLLoading,
		isFetching: getImageURLFetching,
		error: getImageURLError
	} = useGetMediaURLQuery(
		{ filename: `${eventId}_plate.jpeg`, filetype: "plates" },
		{ skip: !eventId || useCroppedImage }
	);

	useEffect(() => {
		if (!canCrop || !sourceImage || !plateCrop) {
			setCroppedImageUrl(null);
			setIsCropping(false);
			setCropFailed(false);
			return;
		}

		let cancelled = false;

		setIsCropping(true);
		setShowError(false);
		setCropFailed(false);
		setCroppedImageUrl(null);

		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			setIsCropping(false);
			setCropFailed(true);
			return;
		}

		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			if (cancelled) return;

			const [x, y, width, height] = plateCrop;

			canvas.width = width;
			canvas.height = height;
			ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

			setCroppedImageUrl(canvas.toDataURL("image/jpeg"));
			setIsCropping(false);
		};
		img.onerror = () => {
			if (cancelled) return;

			setCroppedImageUrl(null);
			setIsCropping(false);
			setCropFailed(true);
		};
		img.src = sourceImage.url;

		return () => {
			cancelled = true;
		};
	}, [canCrop, sourceImage, plateCrop]);

	if (isCropping || (!useCroppedImage && (getImageURLLoading || getImageURLFetching))) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
				<CircularProgress size={20} />
			</Box>
		);
	}

	if (useCroppedImage && croppedImageUrl) {
		return (
			<img
				src={croppedImageUrl}
				alt={`Plate ${eventId}`}
				style={{ width: "100%", height: "100%", objectFit: "contain" }}
				onError={() => setShowError(true)}
			/>
		);
	}

	if (!eventId || showError || getImageURLError || !getImageURLResponse || !getImageURLResponse.url) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
					gap: "0.5rem",
					width: "100%"
				}}
			>
				<NoImageIcon width={20} fill="#d5d5d5" stroke="#d5d5d5" />
			</Box>
		);
	}

	return (
		<img
			src={getImageURLResponse.url}
			alt={`Plate ${eventId}`}
			style={{ width: "100%", height: "100%", objectFit: "contain" }}
			onError={() => setShowError(true)}
		/>
	);
};

export default PlateImage;
